import React from 'react';
import { NextPageContext } from 'next';
import { SWRConfig } from 'swr';

import { Category } from 'models/Category';
import { Product } from 'models/Product';

import { AttendenceType } from 'utils/enums/attendenceType';
import { getAxiosError } from 'utils/getAxiosError';
import { axiosFactory } from 'utils/http/axiosFactory';
import { sendToErrorTracking } from 'utils/sendToErrorTracking';

import CategoryTemplate from 'templates/Category';

interface Props {
  fallback: Category;
}

const Slug: React.FC<Props> = ({ fallback }) => {
  return (
    <SWRConfig value={{ fallback }}>
      <CategoryTemplate />
    </SWRConfig>
  );
};

/*
 * NOTE: This is needed to useRouter be able to hydrate on the first render, if we don't use this useRouter will return undefined on the first render and hydrate after
 * The first undefined is enough to break SWR so we're forcing this hydrate on the first render.
 */
export const getServerSideProps = async ({ query }: NextPageContext) => {
  const locationsQuery = `?locations=`;

  try {
    const categoryListHomecareRequest = axiosFactory().get<Product[]>(
      `/category?attendenceType=${AttendenceType.home}`,
    );

    const categoryListOnlineRequest = axiosFactory().get<Product[]>(
      `/category?attendenceType=${AttendenceType.online}`,
    );

    const categoryRequest = axiosFactory().get<Category>(
      `/category/${query.category}`,
    );

    const [
      categoryListHomecareResponse,
      categoryListOnlineResponse,
      categoryResponse,
    ] = await Promise.all([
      categoryListHomecareRequest,
      categoryListOnlineRequest,
      categoryRequest,
    ]).then((values) => values);

    const categoryProductsPath = `/category/${query.category}/products/${
      categoryResponse?.data?.attendenceType === AttendenceType.home
        ? locationsQuery
        : ''
    }`;

    const categoryProductsResponse = await axiosFactory().get(
      categoryProductsPath,
    );

    return {
      props: {
        fallback: {
          [`/category?attendenceType=${AttendenceType.home}`]:
            categoryListHomecareResponse.data,
          [`/category?attendenceType=${AttendenceType.online}`]:
            categoryListOnlineResponse.data,
          [`/category/${query.category}`]: categoryResponse.data,
          [categoryProductsPath]: categoryProductsResponse.data,
        },
      },
    };
  } catch (error) {
    sendToErrorTracking({
      error,
      where: 'pages/category/getServerSideProps',
      context: {
        query: query,
        attendenceType: AttendenceType.home,
      },
    });

    if (getAxiosError(error).response?.status === 404)
      return {
        notFound: true,
      };
  }
};

export default Slug;
